/* eslint-disable quotes */
import React, { useEffect, useRef, useState } from "react"
import { Formik, Form } from "formik"
import {
  Button,
  Checkbox,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  TextField,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  styled,
} from "@mui/material"
import makeStyles from "@mui/styles/makeStyles"
import dayjs from "dayjs"

import { COLORS, ACTIVE_KFC_BRANDS } from "../../utils"

import Autocomplete from "@mui/material/Autocomplete"
import { replacePackagingsSchema } from "../../utils/yupValidators"
import { formatPackagingsAutocompleteOption } from "../../utils/packaging"
import { useDispatch, useSelector } from "react-redux"
import { getPackagingsSelector } from "../../reducers/Packaging/packagingList"
import { loadPackagings } from "../../actions/Packaging/packaging"

const StyledInputLabel = styled(InputLabel)({
  fontWeight: 400,
  marginBottom: 2,
  fontSize: 18
})

const StyledHelperText = styled(FormHelperText)({
  fontSize: 18,
  marginTop: 4,
  fontWeight: 400
})

const useStyles = makeStyles({
  dialog: {
    maxWidth: 680,
    minHeight: 500,
  },
  formControl: {
    marginLeft: "unset",
    width: "100%",
    marginTop: 20,
    "& *": {
      color: COLORS.BLACK,
    },
    "& .MuiFormLabel-root": {
      position: "relative",
      display: "flex",
      alignItems: "center",
      transform: "none",
      marginTop: 4,
    },
  },
  selectFormControl: {
    composes: "$formControl",
    marginTop: 16,
  },
  dialogTitle: {
    paddingBottom: 0,
  },
})

const normalizeStr = (s) => s.trim().toLowerCase().replace(/\s+/g, " ")

const packagingFilterOptions = (options, { inputValue }) =>
  options.filter((opt) => normalizeStr(opt.label).includes(normalizeStr(inputValue)))

const initialValues = {
  originalPackaging: null,
  replacedPackaging: null,
  brands: [],
}

const downloadCsv = (recipes, values) => {
  const originalLabel = values.originalPackaging ? values.originalPackaging.label : ""
  const replacedLabel = values.replacedPackaging ? values.replacedPackaging.label : ""
  const header = ["ID", "Nom", "Marques impactées", "Packaging remplacé", "Nouveau packaging"].join(",")
  const rows = recipes.map((r) => [
    r.id,
    `"${(r.name || "").replace(/"/g, '""')}"`,
    `"${(r.affectedBrands || []).join(", ")}"`,
    `"${originalLabel.replace(/"/g, '""')}"`,
    `"${replacedLabel.replace(/"/g, '""')}"`,
  ].join(","))
  const csv = [header, ...rows].join("\n")
  const blob = new Blob([" " + csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `apercu-remplacement-packaging-${dayjs().format("YYYY-MM-DD")}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export const ReplacePackagingDialog = ({ onClose, open, onConfirm, onPreview }) => {
  const [packagingOptions, setPackagingOptions] = useState([])
  const [csvDownloaded, setCsvDownloaded] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [selectedOriginal, setSelectedOriginal] = useState(null)
  const [selectedReplaced, setSelectedReplaced] = useState(null)
  const [selectedBrands, setSelectedBrands] = useState([])
  const [brandsMenuOpen, setBrandsMenuOpen] = useState(false)

  const packagings = useSelector(getPackagingsSelector)
  const formikRef = useRef(null)
  const dispatch = useDispatch()

  const classes = useStyles()

  useEffect(() => {
    setPackagingOptions(formatPackagingsAutocompleteOption(packagings))
    dispatch(loadPackagings())
  }, [packagings])

  useEffect(() => {
    setCsvDownloaded(false)
    setSelectedOriginal(null)
    setSelectedReplaced(null)
    setSelectedBrands([])
  }, [open])

  const handleConfirm = () => {
    formikRef.current.submitForm()
    if (!formikRef.current.values.originalPackaging || !formikRef.current.values.replacedPackaging) return
    onClose()
  }

  const handleSubmit = (values) => {
    onConfirm(values)
  }

  const handleCancel = () => {
    onClose()
  }

  const handleDownloadPreview = async () => {
    const values = formikRef.current.values
    setIsLoadingPreview(true)
    try {
      const recipes = await onPreview(values)
      downloadCsv(recipes, values)
      setCsvDownloaded(true)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const canDownload = !isLoadingPreview && selectedOriginal && selectedReplaced && selectedBrands.length > 0
  const canConfirm = selectedOriginal && selectedReplaced && (selectedBrands.length === 0 || csvDownloaded)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        Remplacer un packaging dans toutes les recettes
      </DialogTitle>
      <DialogContent>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={replacePackagingsSchema}
        >
          {({ values, handleChange, handleBlur, setFieldValue, errors }) => {
            return (
              <Form>
                <FormControl
                  className={classes.selectFormControl}
                  variant="standard"
                >
                  <StyledInputLabel>Indiquez l'article fournisseur que vous souhaitez remplacer : </StyledInputLabel>
                  <Autocomplete
                    id="originalPackaging"
                    name="originalPackaging"
                    options={packagingOptions}
                    getOptionLabel={(option) => option.label}
                    filterOptions={packagingFilterOptions}
                    onChange={(_, value) => {
                      if (value) setFieldValue("originalPackaging", value)
                      setSelectedOriginal(value || null)
                      setCsvDownloaded(false)
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.objectId === value.objectId
                    }
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        {...params}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Packaging à remplacer"
                        value={values.originalPackaging ? values.originalPackaging.value : ""}
                        error={!!errors.originalPackaging}
                      />
                    )}
                  />
                  {errors.originalPackaging && (
                    <FormHelperText error>
                      {errors.originalPackaging}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  className={classes.selectFormControl}
                  variant="standard"
                >
                  <StyledHelperText>Indiquez le packaging qui le remplace dans toutes les recettes</StyledHelperText>
                  <Autocomplete
                    id="replacedPackaging"
                    name="replacedPackaging"
                    options={packagingOptions}
                    getOptionLabel={(option) => option.label}
                    filterOptions={packagingFilterOptions}
                    onChange={(_, value) => {
                      if (value) setFieldValue("replacedPackaging", value)
                      setSelectedReplaced(value || null)
                      setCsvDownloaded(false)
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.objectId === value.objectId
                    }
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        {...params}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Packaging de remplacement"
                        value={values.replacedPackaging ? values.replacedPackaging.value : ""}
                      />
                    )}
                  />
                  {errors.replacedPackaging && (
                    <FormHelperText error>
                      {errors.replacedPackaging}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl className={classes.selectFormControl} variant="standard">
                  <StyledInputLabel>Filtrer par marque (optionnel)</StyledInputLabel>
                  <Select
                    multiple
                    name="brands"
                    value={values.brands}
                    open={brandsMenuOpen}
                    onOpen={() => setBrandsMenuOpen(true)}
                    onClose={() => setBrandsMenuOpen(false)}
                    onChange={(e) => {
                      setFieldValue("brands", e.target.value)
                      setSelectedBrands(e.target.value)
                      setCsvDownloaded(false)
                      setBrandsMenuOpen(false)
                    }}
                    displayEmpty
                    renderValue={(selected) =>
                      selected.length === 0
                        ? "Toutes les marques"
                        : selected.map((b) => {
                            const found = ACTIVE_KFC_BRANDS.find((brand) => brand.name === b)
                            return found ? found.label : b
                          }).join(", ")
                    }
                    variant="standard"
                  >
                    {ACTIVE_KFC_BRANDS.map((brand) => (
                      <MenuItem key={brand.name} value={brand.name}>
                        <Checkbox checked={values.brands.includes(brand.name)} size="small" />
                        <ListItemText primary={brand.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Form>
            )
          }}
        </Formik>
      </DialogContent>
      <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", px: 2, pb: 2 }}>
        {selectedOriginal && selectedReplaced && selectedBrands.length > 0 && !csvDownloaded && (
          <FormHelperText sx={{ textAlign: "right", mb: 1, color: COLORS.BLACK }}>
            Téléchargez d'abord l'aperçu CSV pour continuer
          </FormHelperText>
        )}
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Button onClick={handleCancel} color="primary">
            Annuler
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDownloadPreview}
              disabled={!canDownload}
            >
              {isLoadingPreview ? "Chargement..." : "Télécharger l'aperçu CSV"}
            </Button>
            <Button onClick={handleConfirm} color="primary" variant="contained" disabled={!canConfirm}>
              Confirmer
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export default ReplacePackagingDialog
